import Button from "@/components/Button";
import Input from "@/components/Input";
import { TreatmentNameToInitialQuestionnaireIDMapping } from "@/constants/Mappings";
import { MAX_EMAIL_LENGTH } from "@/constants/MaxLengths";
import { PillBGMapping, PillMapping } from "@/utils/discover-mapping";
import { validateEmail } from "@/utils/email";
import {
    CheckCircleIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import Link from "next/link";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useEffect, useState } from "react";

import { addTreatmentWaitlist } from "../mutations/add-waitlist-email";

const TopSection = ({
    treatmentName,
    title,
    subtitle,
    treatmentId,
}: {
    treatmentName: string;
    title: string;
    subtitle: string;
    treatmentId: string;
}) => {
    const router = useRouter();
    const [pillSize, setPillSize] = useState(200);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [emailState, steEmailState] = useState<"success" | "error" | null>(
        null,
    );

    useEffect(() => {
        const updatePillSize = () => {
            setPillSize(window.innerWidth < 768 ? 200 : 400);
        };

        // Set initial size
        updatePillSize();

        // Update on resize
        window.addEventListener("resize", updatePillSize);
        return () => window.removeEventListener("resize", updatePillSize);
    }, []);

    const handleEmailSubmit = async () => {
        if (email.length === 0) {
            setEmailError("Email is required");
            return;
        }
        if (!validateEmail(email).status) {
            setEmailError("Please enter a valid email address");
            return;
        }
        posthog?.capture("treatment_waitlist_email_submitted", {
            treatmentName: treatmentName,
        });

        try {
            await addTreatmentWaitlist({
                email,
                treatment: treatmentName,
            });

            steEmailState("success");
        } catch (error) {
            steEmailState("error");
            console.error(error);
        }
    };

    useEffect(() => {
        steEmailState(null);
    }, [email]);

    const initialQuestionnaireId =
        TreatmentNameToInitialQuestionnaireIDMapping[treatmentName];

    // Handler for email
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputEmail = e.target.value;
        if (emailError) {
            setEmailError(null);
        }
        if (inputEmail.length <= MAX_EMAIL_LENGTH) {
            setEmail(inputEmail.replace(/\s+/g, "").toLocaleLowerCase());
        } else {
            setEmailError(
                `Your email cannot exceed ${MAX_EMAIL_LENGTH} characters in total`,
            );
        }
    };

    const handleEmailBlur = () => {
        if (email.length === 0) {
            setEmailError("Email is required");
        } else {
            const emailValidation = validateEmail(email);
            if (!emailValidation.status) {
                setEmailError(emailValidation.message);
            } else {
                setEmailError(null);
            }
        }
    };

    return (
        <div className="w-full md:p-4 bg-card rounded-2xl flex flex-col md:flex-row gap-8 mt-4">
            {/* Breadcrumbs for mobile - always at top */}
            <div className="md:hidden w-full">
                <div className="flex items-center gap-1 text-sm text-[#1a5b77] dark:text-blue-300 opacity-70 mt-2 ml-2 font-satoshi">
                    <Link
                        href="/treatments"
                        onClick={() => {
                            posthog?.capture("breadcrumb_clicked", {
                                type: treatmentName,
                            });
                        }}
                    >
                        Treatments
                    </Link>
                    <span>/</span>
                    <span>{treatmentName}</span>
                </div>
            </div>

            {/* Image Section - First on mobile */}
            <div className="w-full md:w-2/5 flex justify-center order-1 md:order-2">
                <div
                    className={`${PillBGMapping[treatmentId] ?? "bg-[#cbd9ee]"} rounded-lg flex items-center justify-center p-6 md:p-10`}
                >
                    {PillMapping[treatmentId]?.(pillSize, true)}
                </div>
            </div>

            {/* Content Section */}
            <div className="w-full md:w-3/5 flex flex-col p-3 order-2 md:order-1">
                {/* Breadcrumbs for desktop */}
                <div className="hidden md:flex items-center gap-1 text-sm text-[#1a5b77] dark:text-blue-300 opacity-70 mb-8 font-satoshi">
                    <Link
                        href="/treatments"
                        onClick={() => {
                            posthog?.capture("breadcrumb_clicked", {
                                type: treatmentName,
                            });
                        }}
                    >
                        Treatments
                    </Link>
                    <span>/</span>
                    <span>{treatmentName}</span>
                </div>

                {/* Push content to bottom */}
                <div className="mt-auto flex flex-col gap-8">
                    {/* Header Section */}
                    <div className="flex flex-col gap-4">
                        <h1 className="text-gray-900 dark:text-white text-3xl md:text-5xl font-semibold uppercase leading-tight font-satoshi">
                            {title}
                        </h1>
                        <p className="text-gray-700 dark:text-gray-200 text-lg md:text-xl font-normal leading-relaxed font-space">
                            {subtitle}
                        </p>
                    </div>

                    {/* Button and Note Section */}
                    <div className="flex flex-col gap-4 w-full md:w-3/4">
                        {initialQuestionnaireId ? (
                            <div className="flex flex-col gap-4">
                                <Button
                                    id={`start-safety-assessment-top-${treatmentName}`}
                                    text="Click Here to Start Your Safety Assessment"
                                    type="filled"
                                    onClick={() => {
                                        posthog?.capture(
                                            "start_safety_assessment",
                                            {
                                                part: "top_section",
                                                treatmentName,
                                            },
                                        );
                                        router.push(
                                            `/orders/${initialQuestionnaireId}`,
                                        );
                                    }}
                                    height="h-14"
                                    className="uppercase w-full"
                                />
                                <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base font-light opacity-70 items-center text-center font-space">
                                    Only pay if your prescription is approved
                                </p>
                            </div>
                        ) : (
                            <div className="w-full flex flex-col gap-4">
                                <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base font-light opacity-70 items-center text-center font-space">
                                    Join the waitlist to be notified when this
                                    treatment is available
                                </p>
                                <div className="flex flex-col gap-4">
                                    <div>
                                        <Input
                                            name="waitlist-email"
                                            value={email}
                                            type="email"
                                            placeholder="Email"
                                            className="w-full"
                                            onChange={handleEmailChange}
                                            onBlur={handleEmailBlur}
                                            success={
                                                validateEmail(email).status &&
                                                !emailError
                                            }
                                            error={
                                                emailError
                                                    ? emailError
                                                    : emailState == "error"
                                                      ? "Error submitting email"
                                                      : null
                                            }
                                            leftIcon={
                                                <EnvelopeIcon className="h-6 w-6 text-slate-400" />
                                            }
                                            rightIcon={
                                                emailError ? (
                                                    <ExclamationTriangleIcon className="h-6 w-6 text-red-300" />
                                                ) : validateEmail(email)
                                                      .status ? (
                                                    <CheckCircleIcon className="h-6 w-6 text-green-300" />
                                                ) : null
                                            }
                                        />
                                        {emailState === "success" && (
                                            <p className="text-green-500 text-sm font-light font-space">
                                                Email submitted successfully
                                            </p>
                                        )}
                                        {/* {emailState === "error" && (
                                            <p className="text-red-500 text-sm font-light font-space">
                                                Error submitting email
                                            </p>
                                        )} */}
                                    </div>
                                    <Button
                                        id={`join-waitlist-${treatmentName}`}
                                        text="Join Waitlist"
                                        type="filled"
                                        onClick={handleEmailSubmit}
                                        className="w-full uppercase"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TopSection;
